<template>
  <Teleport v-if="visible" to="body">
    <div
      ref="trapRef"
      class="AiMediaGallery"
      :class="{ 'AiMediaGallery--visible': visible }"
      role="dialog"
      aria-modal="true"
      @scroll.prevent
      @touchmove.prevent
      @keyup.esc.capture="closeGalleryCarousel">
      <div class="Layout AiMediaGallery-layout">
        <section class="Layout-section AiMediaGallery-container">
          <grid-item
            :class="{ 'AiMediaGallery-closeContainer--landscape': landscape }"
            class="AiMediaGallery-closeContainer"
            md="1"
            offset-md="10"
            sm="1"
            offset-sm="3">
            <ai-button
              ref="closeButton"
              variant="tertiary"
              slim
              :aria-label="$t('ux.molecules.modal.close.label')"
              @click="closeGalleryCarousel">
              <ai-icon name="close" />
            </ai-button>
          </grid-item>
          <grid-item md="10" offset-md="1" sm="4">
            <ai-carousel
              v-model="step"
              class="AiMediaGallery-carousel"
              :class="{ 'AiMediaGallery-carousel--landscape': landscape }"
              :items="galleryItems"
              position="full"
              :gutter="24"
              :slide-width="slideWidth"
              :arrows-top-position="
                isSmallDevice ? (landscape ? undefined : '70%') : '45%'
              "
              :arrows-right-position="
                isSmallDevice ? (landscape ? '5%' : '85%') : undefined
              "
              :button-accessibility-label="buttonAccessibilityLabel"
              hide-gallery
              sync-tire-with-slide />
          </grid-item>
        </section>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useLayoutStore } from '../../layout.store';
import AiCarousel from '../AiCarousel/AiCarousel.vue';
import type { AiCarouselItem } from '../AiCarousel/types';

import { useFocusTrap } from '~~/helpers/modal.helper';

interface Props {
  modelValue: boolean;
  step: number;
  items: AiCarouselItem[];
  galleryComponent?: Component | keyof HTMLElementTagNameMap;
  buttonAccessibilityLabel?: string;
}

interface Emits {
  (event: 'update:modelValue', value: boolean): void;
  (event: 'focusGalleryButton'): void;
  (event: 'update:step', value: number): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const layoutStore = useLayoutStore();

const closeButton = ref<ComponentPublicInstance | undefined>();

const step = computed({
  get() {
    return props.step;
  },
  set(value: Props['step']) {
    emits('update:step', value);
  },
});

const visible = computed<boolean>({
  get() {
    return props.modelValue;
  },
  set(value) {
    emits('update:modelValue', value);
  },
});

const closeGalleryCarousel = () => {
  emits('update:modelValue', false);
  emits('focusGalleryButton');
};

const { trapRef } = useFocusTrap();

const isSmallDevice = useMatchMediaQuery('desktop');
const slideWidth = computed(() => (isSmallDevice.value ? 0.9 : 1));

const landscape = ref<boolean>(false);
const screenOrientationListener = () => {
  landscape.value = getOrientation().includes('landscape');
};

onMounted(() => {
  landscape.value = getOrientation().includes('landscape');

  if (window?.screen?.orientation) {
    window.screen.orientation.addEventListener(
      'change',
      screenOrientationListener,
    );
  } else {
    window.addEventListener('orientationchange', screenOrientationListener);
  }
});

onUnmounted(() => {
  if (window?.screen?.orientation) {
    window.screen.orientation.removeEventListener(
      'change',
      screenOrientationListener,
    );
  } else {
    window.removeEventListener('orientationchange', screenOrientationListener);
  }
});

const galleryItems = computed<AiCarouselItem[]>(() =>
  props.galleryComponent
    ? (props.items.map(item => ({
        ...item,
        component: props.galleryComponent,
        key: `media-gallery-${item.key}`,
      })) as AiCarouselItem[])
    : props.items,
);

watch(
  () => visible.value,
  value => {
    layoutStore.setScrollLocked(value);
  },
);

const getOrientation = () => {
  if (!window?.screen?.orientation?.type) {
    // We are on Apple
    const orientation = Math.abs(window.orientation);

    if (orientation === 90) {
      return 'landscape';
    }

    return 'portrait';
  }

  return window.screen.orientation.type;
};
</script>

<style lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.AiMediaGallery-layout {
  width: 100%;
}

.AiMediaGallery {
  position: fixed;
  z-index: -1;
  inset: 0;
  display: flex;
  align-items: center;
  background-color: rgba(colors.$neutral-800, 0.95);
  visibility: hidden;
  opacity: 0;
  transition: opacity 500ms;
  overflow: hidden;

  &--visible {
    visibility: visible;
    z-index: 200;
    opacity: 1;
  }
}

.AiMediaGallery-carousel {
  .AiCarousel-container {
    height: 75vh;
    width: 100%;

    .AiImage {
      height: 100%;
      max-height: 33vh;

      @media (mq.$from-medium) {
        max-height: none;
      }
    }
  }

  &--landscape {
    .AiCarousel-container .AiImage {
      max-height: none;
    }

    .Carousel-actions {
      margin-top: constants.$margin-02;
    }
  }

  .Carousel-arrows {
    @media (mq.$upto-medium) {
      display: inherit;
      top: 75%;
      right: unset;
    }
  }
}

.AiMediaGallery-closeContainer {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: constants.$margin-03;

  .AiIcon {
    fill: colors.$white;
  }

  &--landscape {
    margin-bottom: constants.$margin-01;
  }
}
</style>
